import { convertDateToApiParamDateTime } from '../../../helpers/date'
import UpToAndIncludingDate from '../../../helpers/date/UpToAndIncludingDate'
import { LeaveOfAbsenceFormData } from '../../../models'
import { authorizedFetch } from '../../helpers/authorizedFetch'
import { formatDate } from '../../helpers/date'
import trans from '../../helpers/trans'
import { generateApiUrl, generateApiV3Url } from '../../helpers/url'
import getIncluded from '../../japi/getIncluded'
import isResourceDocument from '../../japi/guards/isResourceDocument'
import { JapiDocument } from '../../japi/types/Document'
import { DateFormat } from '../../types/dateFormatTypes'
import { FetchResult, FetchResultType } from '../FetchResult'
import { NotificationCenterHeaderName, NotificationCenterLog } from '../NotificationCenter/NotificationCenter'
import { Period } from '../Period/Period'
import { UserResource } from '../User/User'
import { transformBasicUserResourceToBasicV3User, transformToBasicUser } from '../User/UserTransformers'
import { LeaveOfAbsence, LeaveOfAbsenceResource, LeaveOfAbsenceV3Resource } from './LeaveOfAbsence'
import { transformToLeaveOfAbsence, transformV3ResourceToLeaveOfAbsence } from './LeaveOfAbsenceTransformers'

let getLeaveOfAbsencesInPeriodApiCallAbortController = new AbortController()

export const getLeaveOfAbsencesInPeriodApiCall = async (period: Period, isCancelable = false): Promise<FetchResult<LeaveOfAbsence[], string>> => {
    if (isCancelable) {
        getLeaveOfAbsencesInPeriodApiCallAbortController.abort()
        getLeaveOfAbsencesInPeriodApiCallAbortController = new AbortController()
    }

    try {
        const url = generateApiV3Url({
            endpoint: '/leave-of-absences/in-period-with-user',
            queryParams: {
                start: formatDate(period.start, DateFormat.apiDateTime),
                end: formatDate(period.end, DateFormat.apiDateTime),
            },
        })

        const response = await authorizedFetch(url, { signal: getLeaveOfAbsencesInPeriodApiCallAbortController.signal })

        const doc: LeaveOfAbsenceV3Resource[] = await response.json()

        if (! doc) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const leaveOfAbsences = doc.map(leaveOfAbsenceResource => {
            const basicUser = transformBasicUserResourceToBasicV3User(leaveOfAbsenceResource.user)

            return transformV3ResourceToLeaveOfAbsence(leaveOfAbsenceResource, basicUser)
        })

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: leaveOfAbsences,
        }
    } catch (error) {
        console.error('[getLeaveOfAbsencesInPeriodApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const postLeaveOfAbsenceApiCall = async (
    data: LeaveOfAbsenceFormData,
    userId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<LeaveOfAbsence, string>> => {
    // TODO: Integrate UpToAndIncludingDate and UpToButExcludingDate in leaveOfAbsences data models.
    const transformedData = {
        ...data,
        end: data.end ? convertDateToApiParamDateTime(
            new UpToAndIncludingDate(data.end).transformToUpToButExcludingDate().date,
        ) : data.end,
        start: convertDateToApiParamDateTime(data.start),
        status: 'approved',
    }

    try {
        const includes = ['user']

        const url = generateApiUrl({
            endpoint: '/leave-of-absences',
            queryParams: {
                include: includes.join(','),
            },
        })

        const response = await authorizedFetch(url, {
            method: 'POST',
            body: JSON.stringify({
                data: {
                    type: 'leaveOfAbsences',
                    attributes: transformedData,
                    relationships: {
                        user: {
                            data: {
                                type: 'users',
                                id: userId,
                            },
                        },
                    },
                },
            }),
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<LeaveOfAbsenceResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            }
        }

        const userResource = getIncluded(doc, doc.data, 'user') as UserResource
        const user = transformToBasicUser(userResource)
        const leaveOfAbsence = transformToLeaveOfAbsence(doc.data, user)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: leaveOfAbsence,
        }
    } catch (error) {
        console.error('[postLeaveOfAbsenceApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const patchLeaveOfAbsenceApiCall = async (
    leaveOfAbsenceId: string,
    data: LeaveOfAbsenceFormData,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<LeaveOfAbsence, string>> => {
    // TODO: Integrate UpToAndIncludingDate and UpToButExcludingDate in leaveOfAbsences data models.
    const transformedData = {
        ...data,
        end: data.end ? convertDateToApiParamDateTime(
            new UpToAndIncludingDate(data.end).transformToUpToButExcludingDate().date,
        ) : data.end,
    }

    try {
        const includes = ['user']

        const url = generateApiUrl({
            endpoint: `/leave-of-absences/${leaveOfAbsenceId}`,
            queryParams: {
                include: includes.join(','),
            },
        })

        const response = await authorizedFetch(url, {
            method: 'PATCH',
            body: JSON.stringify({
                data: {
                    id: leaveOfAbsenceId,
                    type: 'leaveOfAbsences',
                    attributes: transformedData,
                },
            }),
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<LeaveOfAbsenceResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            }
        }

        const userResource = getIncluded(doc, doc.data, 'user') as UserResource
        const user = transformToBasicUser(userResource)
        const leaveOfAbsence = transformToLeaveOfAbsence(doc.data, user)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: leaveOfAbsence,
        }
    } catch (error) {
        console.error('[patchLeaveOfAbsenceApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const deleteLeaveOfAbsenceApiCall = async (
    leaveOfAbsenceId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<any, string>> => {
    try {
        const url = generateApiUrl({
            endpoint: `/leave-of-absences/${leaveOfAbsenceId}`,
        })

        const response = await authorizedFetch(url, {
            method: 'DELETE',
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: {},
        }
    } catch (error) {
        console.error('[deleteLeaveOfAbsenceApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const postLeaveOfAbsenceApproveApiCall = async (
    leaveOfAbsenceId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<any, string>> => {
    try {
        const url = generateApiUrl({
            endpoint: `/leave-of-absences/${leaveOfAbsenceId}/approve`,
        })

        const response = await authorizedFetch(url, {
            method: 'POST',
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        const doc: JapiDocument = await response.json()

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: {},
        }
    } catch (error) {
        console.error('[postLeaveOfAbsenceAccept]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const postLeaveOfAbsenceDeclineApiCall = async (
    leaveOfAbsenceId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<any, string>> => {
    try {
        const url = generateApiUrl({
            endpoint: `/leave-of-absences/${leaveOfAbsenceId}/decline`,
        })

        const response = await authorizedFetch(url, {
            method: 'POST',
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        const doc: JapiDocument = await response.json()

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: {},
        }
    } catch (error) {
        console.error('[postLeaveOfAbsenceDecline]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}
